import { useEffect } from 'react';
import { Download, X } from 'lucide-react';
import { ComputedSectionView } from './ComputedSectionView';
import { NarrativePreview } from './NarrativePreview';
import {
  SectionEvidenceTable,
  evidenceForSectionKey,
  type EvidenceRow,
} from './EvidenceTable';
import { evidenceDownloadUrl } from '../../../lib/api/queries';
import type {
  ScoreChartStyle,
  AuditReport, ReportSection, ReportSnapshotContent, ReportSnapshotSection,
} from '../../../lib/api/queries';

/**
 * Minimal section shape the preview needs. Both live ReportSection and
 * historical ReportSnapshotSection match it, so the same modal renders
 * both.
 */
type PreviewableSection = Pick<
  ReportSection,
  'id' | 'key' | 'title' | 'displayOrder' | 'kind' | 'computedKind' | 'contentJson'
>;

/**
 * Full-page modal showing the entire report assembled in display order:
 * narrative sections rendered read-only, computed sections rendered live
 * from the audit data. Mirrors what the exported PDF will produce.
 *
 * Accepts either the live AuditReport or a ReportSnapshotContent (saved
 * snapshot) — the auditor can preview "v1.0 we sent to the client" the
 * same way they preview the current draft. Computed sections always
 * render live (they re-derive from the audit form); narrative sections
 * use whatever ContentJson was passed in.
 *
 * Scrollable body so long reports don't blow the viewport. Esc closes;
 * Download PDF lives in the header for "ship it" moments.
 */
export function ReportPreviewModal({
  tenantId,
  auditId,
  source,
  onClose,
}: {
  tenantId: string;
  auditId: string;
  /**
   * Either the live report or a historical snapshot. The discriminating
   * field is `snapshot` — when set, the modal header shows the snapshot
   * label/timestamp so the auditor knows they're looking at a frozen
   * version.
   */
  source:
    | { kind: 'live'; report: AuditReport }
    | { kind: 'snapshot'; content: ReportSnapshotContent };
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const sections: PreviewableSection[] = source.kind === 'live'
    ? source.report.sections
    : asPreviewable(source.content.sections);

  // A saved version draws the chart it was saved with, not today's choice.
  const scoreChart = source.kind === 'live' ? source.report.scoreChart : source.content.scoreChart;

  // Slice 17 — the live report's current attachments, or the set frozen
  // into the snapshot being previewed. Same normalisation either way, so
  // the preview matches the PDF that would be generated from this source.
  const evidenceRows: EvidenceRow[] = (source.kind === 'live'
    ? source.report.evidence
    : source.content.evidence
  ).map(e => ({
    id:            e.id,
    sectionId:     e.sectionId,
    sectionCode:   e.sectionCode,
    criterionCode: e.criterionCode,
    itemText:      e.itemText,
    referenceCode: e.referenceCode,
    fileName:      e.fileName,
    sizeBytes:     e.sizeBytes,
    description:   e.description,
    itemComment:   e.itemComment,
    uploadedAtUtc: e.uploadedAtUtc,
    blocked:       e.scanStatus === 'Infected',
    href:          evidenceDownloadUrl(auditId, e.id),
  }));

  const title = source.kind === 'live'
    ? 'Report preview'
    : `Report preview — ${source.content.label
        ?? `Version saved ${new Date(source.content.createdAtUtc).toLocaleString()}`}`;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-stretch justify-center"
         onClick={onClose}>
      <div className="bg-white w-full max-w-4xl my-6 mx-4 rounded-lg shadow-xl overflow-hidden
                      flex flex-col"
           onClick={e => e.stopPropagation()}>
        <header className="flex items-center justify-between px-6 py-3 border-b border-gray-200
                           bg-gray-50 shrink-0">
          <h2 className="text-base font-semibold text-gray-900 truncate">{title}</h2>
          <div className="flex items-center gap-2 shrink-0">
            <a href={`/api/audit/audits/${auditId}/report/pdf`} download
               className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium
                          text-white bg-accent-alt-600 hover:bg-accent-alt-700 rounded-md
                          transition-colors whitespace-nowrap">
              <Download className="w-4 h-4" aria-hidden /> Download PDF
            </a>
            <button type="button" onClick={onClose}
                    title="Close (Esc)"
                    className="inline-flex items-center justify-center w-8 h-8 rounded-md
                               text-gray-500 hover:bg-gray-100 transition-colors">
              <X className="w-5 h-5" aria-hidden />
            </button>
          </div>
        </header>

        <div className="overflow-y-auto px-8 py-6 space-y-8 bg-white">
          {sections.map(section => (
            <PreviewSection key={section.id}
                            tenantId={tenantId}
                            auditId={auditId}
                            section={section}
                            evidenceRows={evidenceRows}
                            scoreChart={scoreChart} />
          ))}
        </div>
      </div>
    </div>
  );
}

function PreviewSection({
  tenantId, auditId, section, evidenceRows, scoreChart,
}: {
  tenantId: string;
  auditId: string;
  section: PreviewableSection;
  evidenceRows: EvidenceRow[];
  scoreChart: ScoreChartStyle;
}) {
  return (
    <section className="space-y-3">
      <h2 className="text-xl font-semibold text-brand-700 border-b border-gray-200 pb-1">
        {section.title}
      </h2>
      {section.kind === 'Narrative' ? (
        <>
          <NarrativePreview contentJson={section.contentJson} />
          <SectionEvidenceTable
            rows={evidenceForSectionKey(section.key, evidenceRows)}
          />
        </>
      ) : (
        // ComputedSectionView wants a full ReportSection. The fields
        // beyond PreviewableSection (revision metadata, last-updated) are
        // only used by the editor; safe to default them.
        <ComputedSectionView
          tenantId={tenantId}
          auditId={auditId}
          section={asFullSection(section)}
          evidenceRows={evidenceRows}
          scoreChart={scoreChart} />
      )}
    </section>
  );
}

function asPreviewable(sections: ReportSnapshotSection[]): PreviewableSection[] {
  return sections.map(s => ({
    id:           s.id,
    key:          s.key,
    title:        s.title,
    displayOrder: s.displayOrder,
    kind:         s.kind,
    computedKind: s.computedKind,
    contentJson:  s.contentJson,
  }));
}

function asFullSection(s: PreviewableSection): ReportSection {
  return {
    ...s,
    currentRevision:     null,
    lastUpdatedAtUtc:    null,
    lastUpdatedByUserId: null,
  };
}
